import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Loader2, UserCircle, ShieldCheck, FileText, 
  CheckCircle, Star, Clock, MapPin, Truck, ChevronLeft,
  Briefcase, CheckSquare, Award, ArrowRight
} from "lucide-react";

import Sidebar from "../Includes/Sidebar";
import TopBar from "../Includes/TopBar";
import BottomNav from "../Includes/BottomNav";

// ─── ROUTE MAP COMPONENT ──────────────────────────────────────────────────────
function RouteMap({ origin, destination, originCoords, destCoords, meetingCoords, meetingName, distanceMiles }) {
  const fromCoords = originCoords || [39.8283, -98.5795];
  const toCoords   = destCoords || [39.8283, -98.5795];

  const midLat  = (fromCoords[0] + toCoords[0]) / 2;
  const midLng  = (fromCoords[1] + toCoords[1]) / 2;
  const dLat    = toCoords[0] - fromCoords[0];
  const dLng    = toCoords[1] - fromCoords[1];
  const distMi  = distanceMiles ? Math.round(distanceMiles) : (() => { const r = (x) => (x * Math.PI) / 180; const h = Math.sin(r(dLat) / 2) ** 2 + Math.cos(r(fromCoords[0])) * Math.cos(r(toCoords[0])) * Math.sin(r(dLng) / 2) ** 2; return Math.round(2 * 3958.8 * Math.asin(Math.sqrt(h)) * 1.18); })();
  const zoom = distMi < 80 ? 8 : distMi < 200 ? 7 : distMi < 400 ? 6 : 5;

  const mapHtml = `<!DOCTYPE html>
<html><head>
<meta charset="utf-8"/>
<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css"/>
<script src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js"></script>
<style>*{margin:0;padding:0;box-sizing:border-box;}html,body,#map{width:100%;height:100%;}</style>
</head><body>
<div id="map"></div>
<script>
var map = L.map('map', {zoomControl:true, attributionControl:false}).setView([${midLat},${midLng}],${zoom});
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{maxZoom:18}).addTo(map);

var fromPt = [${fromCoords[0]},${fromCoords[1]}];
var toPt   = [${toCoords[0]},${toCoords[1]}];
var dLat = toPt[0] - fromPt[0];
var dLng = toPt[1] - fromPt[1];
var pts = [];
var steps = 20;
for(var i=0;i<=steps;i++){
  var t = i/steps;
  var cx = (fromPt[1]+toPt[1])/2 + dLat*0.15;
  var cy = (fromPt[0]+toPt[0])/2 - dLng*0.15;
  var lat = (1-t)*(1-t)*fromPt[0] + 2*(1-t)*t*cy + t*t*toPt[0];
  var lng = (1-t)*(1-t)*fromPt[1] + 2*(1-t)*t*cx + t*t*toPt[1];
  pts.push([lat,lng]);
}

L.polyline(pts,{color:'#0099a8',weight:4,opacity:0.85,dashArray:null}).addTo(map);
L.polyline(pts,{color:'#005f6b',weight:6,opacity:0.2}).addTo(map);

var originIcon = L.divIcon({html:'<div style="background:#e07b00;width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>',iconAnchor:[7,7],className:''});
var destIcon   = L.divIcon({html:'<div style="background:#0099a8;width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);"></div>',iconAnchor:[9,9],className:''});

L.marker(fromPt,{icon:originIcon}).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">${origin.replace(/'/g,"\\'")}</b>',{permanent:false,direction:'top'});
L.marker(toPt,  {icon:destIcon  }).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">${destination.replace(/'/g,"\\'")}</b>',{permanent:false,direction:'top'});

${meetingCoords && meetingCoords[0] ? `
var meetingPt = [${meetingCoords[0]},${meetingCoords[1]}];
var meetIcon = L.divIcon({html:'<div style="background:#7c3aed;width:14px;height:14px;border:3px solid white;border-radius:3px;box-shadow:0 2px 6px rgba(0,0,0,0.4);transform:rotate(45deg);"></div>',iconAnchor:[7,7],className:''});
var meetName = "${meetingName ? meetingName.split(",")[0].replace(/"/g, '\\"').replace(/'/g, "\\'") : 'Meeting Point'}";
L.marker(meetingPt,{icon:meetIcon}).addTo(map).bindTooltip('<b style="font-family:sans-serif;font-size:12px">🚩 ' + meetName + '</b>',{permanent:true,direction:'top', offset:[0,-10]});
var bounds = L.latLngBounds([fromPt, toPt, meetingPt]);
map.fitBounds(bounds, {padding: [30, 30]});
` : ''}
</script>
</body></html>`;

  return (
    <div className="rounded-xl overflow-hidden border border-gray-200 shadow-inner bg-gray-50 h-48 sm:h-56 relative w-full mb-4">
      <iframe srcDoc={mapHtml} style={{ width:"100%", height:"100%", border:"none", display:"block" }} title="Route Map" />
      <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur border border-gray-200/50 rounded-lg p-2 flex items-center justify-between pointer-events-none shadow-sm">
         <span className="text-[10px] font-bold text-gray-700 truncate max-w-[40%]">📍 {origin.split(",")[0]}</span>
         <span className="text-[10px] font-bold text-blue-600">~{distMi} miles</span>
         <span className="text-[10px] font-bold text-gray-700 truncate max-w-[40%] text-right">🏁 {destination.split(",")[0]}</span>
      </div>
    </div>
  );
}

// ─── LOAD CONTEXT SUMMARY COMPONENT ───────────────────────────────────────────
function LoadContextSummary({ load }) {
  if (!load) return null;
  const origin = load.from || load.origin || load.origin_label || "—";
  const destination = load.to || load.destination || load.destination_label || "—";
  const hasMap = load.startCoords && load.endCoords;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 shadow-sm mb-4 sm:mb-6">
      <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
        <MapPin className="w-4 h-4 text-blue-600" /> Route Overview
      </h3>
      
      {hasMap && (
        <RouteMap 
          origin={origin} 
          destination={destination} 
          originCoords={load.startCoords} 
          destCoords={load.endCoords}
          meetingCoords={load.meetingCoords}
          meetingName={load.meeting_point}
        />
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Origin / Dest Card */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100 h-full">
           <div className="flex-1 min-w-0">
             <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Origin</p>
             <p className="text-sm font-bold text-gray-900 leading-snug break-words">{origin}</p>
             {load.origin_zip && <p className="text-xs text-gray-500 mt-0.5">ZIP: {load.origin_zip}</p>}
             <p className="text-xs font-semibold text-blue-600 mt-2 flex items-center gap-1">
               <Clock className="w-3.5 h-3.5" /> {load.date || "TBD"} {load.time ? `· 🕐 ${load.time}` : ""}
             </p>
           </div>
           <ArrowRight className="hidden sm:block w-5 h-5 text-gray-300 flex-shrink-0" />
           <div className="flex-1 min-w-0 sm:text-right border-t sm:border-t-0 border-gray-200 pt-3 sm:pt-0">
             <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">Destination</p>
             <p className="text-sm font-bold text-gray-900 leading-snug break-words">{destination}</p>
             {load.destination_zip && <p className="text-xs text-gray-500 mt-0.5">ZIP: {load.destination_zip}</p>}
           </div>
        </div>

        {/* Meeting Point Card */}
        {load.meeting_point ? (
          <div className="bg-purple-50/50 border border-purple-100 rounded-xl p-4 h-full flex flex-col justify-center">
            <p className="text-[10px] font-bold uppercase tracking-widest text-purple-500 mb-1 flex items-center gap-1">
              <span>🚩</span> Meeting Point
            </p>
            <p className="text-sm font-bold text-gray-900 break-words">{load.meeting_point}</p>
            <div className="flex flex-col gap-1 mt-1 text-xs text-gray-600">
              {load.meeting_city && <span>{load.meeting_city}, {load.meeting_state} {load.meeting_zip}</span>}
              {load.meeting_time && <span className="font-semibold text-purple-700">Time: {load.meeting_time}</span>}
            </div>
          </div>
        ) : (
           <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 flex flex-col justify-center items-center text-gray-400 h-full">
             <span className="text-[10px] font-bold uppercase tracking-widest mb-1">Meeting Point</span>
             <span className="text-xs italic">Not specified</span>
           </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN APPLICANTS PAGE ─────────────────────────────────────────────────────
export default function LoadApplicantsPage() {
  const { loadId } = useParams();
  const navigate = useNavigate();
  
  const [user, setUser] = useState(null);
  const [loadDetails, setLoadDetails] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [assigningId, setAssigningId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dispatchers, setDispatchers] = useState([]);
  const [selectedDispatcherId, setSelectedDispatcherId] = useState("");

  const handleLogout = () => {
    localStorage.clear();
    navigate("/company-login");
  };

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) navigate("/company-login");
    setUser(storedUser);
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
        const token  = localStorage.getItem("user_token") || localStorage.getItem("access_token");
        const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };

        // 1. Fetch Load Details (Map, Route, Meeting Point)
        const loadRes = await fetch(`${apiUrl}/api/loads/${loadId}/`, { headers });
        if (loadRes.ok) {
          const loadData = await loadRes.json();
          setLoadDetails(loadData);
        }

        // 2. Fetch Applicants
        const appRes = await fetch(`${apiUrl}/api/loads/${loadId}/applicants/`, { headers });
        if (appRes.ok) {
          const appData = await appRes.json();
          setApplicants(appData);
          if (appData.length > 0 && window.innerWidth >= 1024) {
            setSelectedApplicant(appData[0]);
          }
        }

        // 3. Fetch this company's dispatchers, so one can be assigned alongside the driver
        const dispRes = await fetch(`${apiUrl}/api/company/dispatchers/`, { headers });
        if (dispRes.ok) {
          const dispData = await dispRes.json();
          const active = dispData.filter(d => d.is_active);
          setDispatchers(active);
          if (active.length === 1) setSelectedDispatcherId(String(active[0].id));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setIsLoading(false);
      }
    };
    if (loadId) fetchData();
  }, [loadId]);

  const handleAssignDriver = async (driverId) => {
    setAssigningId(driverId);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
      const token  = localStorage.getItem("user_token") || localStorage.getItem("access_token");

      const res = await fetch(`${apiUrl}/api/loads/${loadId}/assign/`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({
          driver_id: driverId,
          ...(selectedDispatcherId ? { dispatcher_id: selectedDispatcherId } : {}),
        })
      });

      if (res.ok) {
        alert("Driver successfully assigned! Live tracking is now enabled.");
        navigate("/company-dashboard"); 
      } else {
        const err = await res.json();
        alert(err.error || "Failed to assign driver.");
      }
    } catch (err) {
      alert("Network error occurred while trying to assign driver.");
    } finally {
      setAssigningId(null);
    }
  };

  const companyName = user?.company_name || user?.name || "Company";
  const initials = companyName.split(" ").slice(0, 2).map(w => w[0]).join("").toUpperCase() || "CO";

  const formatTime = (dateStr) => {
    if (!dateStr) return "N/A";
    const dt = new Date(dateStr);
    return dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      <Sidebar activeNav="dashboard" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} initials={initials} companyName={companyName} handleLogout={handleLogout}/>
      
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar title="Review Applicants" setSidebarOpen={setSidebarOpen} initials={initials} notifCount={0} handleLogout={handleLogout}/>
        
        <main className="flex-1 overflow-y-auto p-0 sm:p-4 lg:p-6">
          <div className="max-w-6xl mx-auto h-full flex flex-col">
            
            {/* Header */}
            <div className="flex items-center gap-4 px-4 sm:px-0 py-4 sm:py-2 mb-2 sm:mb-4 flex-shrink-0">
              <button onClick={() => navigate("/company-dashboard")} className="p-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition shadow-sm">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-gray-900">Load #{loadDetails?.id || loadId} Applicants</h1>
                <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Review driver experience, bids, and certificates.</p>
              </div>
            </div>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center flex-1 bg-white rounded-2xl border border-gray-100 m-4 sm:m-0">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-4" />
                <p className="text-sm text-gray-500 font-medium">Fetching load and driver data...</p>
              </div>
            ) : (
              <>
                {/* 🗺️ LOAD CONTEXT SUMMARY 🗺️ */}
                <LoadContextSummary load={loadDetails} />

                {/* 👥 APPLICANTS AREA 👥 */}
                {applicants.length === 0 ? (
                  <div className="flex flex-col items-center justify-center flex-1 bg-white border border-gray-200 rounded-2xl p-10 text-center shadow-sm">
                    <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 border border-gray-100">
                      <UserCircle className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">No applicants yet</h3>
                    <p className="text-sm text-gray-500 mt-1 max-w-md">
                      Drivers in the area have been notified. Check back soon. 
                      Featured loads generally get matches 3x faster.
                    </p>
                    <button onClick={() => navigate("/company-dashboard")} className="mt-6 px-6 py-2.5 bg-[#1a2744] text-white text-sm font-bold rounded-xl hover:opacity-90 transition">
                      Return to Dashboard
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col lg:flex-row flex-1 overflow-hidden sm:gap-6 bg-white sm:bg-transparent">
                    
                    {/* LEFT SIDE: Applicant List */}
                    <div className={`w-full lg:w-1/3 flex-shrink-0 flex flex-col bg-gray-50 sm:bg-white sm:border border-gray-200 sm:rounded-2xl overflow-hidden ${selectedApplicant ? 'hidden lg:flex' : 'flex'}`}>
                      <div className="p-4 border-b border-gray-200 bg-white">
                        <h2 className="text-sm font-bold text-gray-800">
                          {applicants.length} Applicant{applicants.length !== 1 && 's'}
                        </h2>
                      </div>
                      <div className="flex-1 overflow-y-auto p-2 sm:p-3 space-y-2">
                        {applicants.map(app => (
                          <div 
                            key={app.id} 
                            onClick={() => setSelectedApplicant(app)}
                            className={`p-3 rounded-xl border cursor-pointer transition-all ${
                              selectedApplicant?.id === app.id 
                                ? "bg-blue-50 border-blue-200 shadow-sm" 
                                : "bg-white border-gray-100 hover:border-blue-100 hover:shadow-sm"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                                <UserCircle className="w-6 h-6 text-gray-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-bold text-gray-900 truncate flex items-center gap-1.5">
                                  {app.driver_name}
                                  <ShieldCheck className="w-3.5 h-3.5 text-green-500" />
                                </h3>
                                <div className="flex items-center gap-1 text-[10px] text-amber-600 font-semibold mt-0.5">
                                  <Star className="w-3 h-3 fill-amber-500 stroke-amber-500" /> {app.rating || "4.9"} Rating
                                </div>
                              </div>
                            </div>
                            <div className="mt-3 flex items-center justify-between text-[11px]">
                              <span className="text-gray-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" /> {formatTime(app.applied_at)}
                              </span>
                              {app.is_counter_offer ? (
                                <span className="font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded border border-amber-100">Counter Bid</span>
                              ) : (
                                <span className="font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded border border-green-100">Match Rate</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* RIGHT SIDE: Driver Detail View */}
                    <div className={`w-full lg:w-2/3 flex-col bg-white sm:border border-gray-200 sm:rounded-2xl overflow-hidden shadow-sm ${selectedApplicant ? 'flex' : 'hidden lg:flex'}`}>
                      {selectedApplicant ? (
                        <>
                          <div className="p-5 border-b border-gray-100 flex items-start gap-4 bg-white relative">
                            <button onClick={() => setSelectedApplicant(null)} className="lg:hidden absolute top-5 right-5 p-2 bg-gray-50 rounded-lg text-gray-600 hover:bg-gray-100">
                              <ChevronLeft className="w-5 h-5" />
                            </button>
                            
                            <div className="w-16 h-16 bg-blue-50 border-2 border-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <UserCircle className="w-10 h-10 text-blue-500" />
                            </div>
                            <div className="pt-1 pr-10 lg:pr-0">
                              <h2 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                                {selectedApplicant.driver_name}
                                <ShieldCheck className="w-5 h-5 text-green-500" />
                              </h2>
                              <div className="flex items-center gap-3 mt-1.5">
                                <span className="text-sm font-semibold text-amber-500 flex items-center gap-1">
                                  <Star className="w-4 h-4 fill-amber-500" /> {selectedApplicant.rating || "4.9"} ({(selectedApplicant.reviews_count || 124)} reviews)
                                </span>
                                <span className="text-gray-300">•</span>
                                <span className="text-sm text-gray-500">{selectedApplicant.driver_phone || "Phone hidden until assigned"}</span>
                              </div>
                            </div>
                          </div>

                          <div className="flex-1 overflow-y-auto p-5 space-y-6 bg-gray-50/30">
                            
                            {/* EXPERIENCE & STATS */}
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Driver Experience</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm text-center">
                                  <Briefcase className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                                  <p className="text-lg font-black text-gray-900">{selectedApplicant.experience_years || "5"} yrs</p>
                                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">Experience</p>
                                </div>
                                <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm text-center">
                                  <CheckSquare className="w-5 h-5 text-green-500 mx-auto mb-1" />
                                  <p className="text-lg font-black text-gray-900">{selectedApplicant.completed_loads || "142"}</p>
                                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">Loads Completed</p>
                                </div>
                                <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm text-center">
                                  <Award className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                                  <p className="text-lg font-black text-gray-900">100%</p>
                                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">On-Time Rate</p>
                                </div>
                                <div className="bg-white border border-gray-100 rounded-xl p-3 shadow-sm text-center">
                                  <MapPin className="w-5 h-5 text-rose-500 mx-auto mb-1" />
                                  <p className="text-lg font-black text-gray-900">{selectedApplicant.states_certified || "12"}</p>
                                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">States Certified</p>
                                </div>
                              </div>
                            </div>

                            {/* BIDDING & TERMS */}
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Application Terms</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className={`p-4 rounded-xl border ${selectedApplicant.is_counter_offer ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                                    {selectedApplicant.is_counter_offer ? 'Driver Counter-Offer' : 'Rate Accepted'}
                                  </p>
                                  <p className={`text-lg font-black ${selectedApplicant.is_counter_offer ? 'text-amber-700' : 'text-green-700'}`}>
                                    {selectedApplicant.is_counter_offer ? (
                                      selectedApplicant.bid_type === 'flat_rate' ? `$${selectedApplicant.bid_amount} Flat` 
                                      : `$${selectedApplicant.bid_amount} / ${selectedApplicant.bid_type.replace('_', ' ')}`
                                    ) : "Will match posted rate"}
                                  </p>
                                </div>

                                <div className="p-4 rounded-xl border border-gray-200 bg-white shadow-sm">
                                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Estimated ETA</p>
                                  <p className="text-sm font-bold text-gray-900 flex items-center gap-1.5 mt-2">
                                    <Clock className="w-4 h-4 text-blue-500" />
                                    {selectedApplicant.estimated_eta ? formatTime(selectedApplicant.estimated_eta) : "Will arrive on time"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* DRIVER MESSAGE */}
                            {selectedApplicant.driver_notes && (
                              <div>
                                <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Message from Driver</p>
                                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                                  <p className="text-sm text-gray-700 italic">"{selectedApplicant.driver_notes}"</p>
                                </div>
                              </div>
                            )}

                            {/* VERIFICATION & CERTS */}
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Verified Documents & Equipment</p>
                              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                                  
                                  <div className="p-4 space-y-3">
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-500"/> Commercial Ins.</span>
                                      <span className="text-xs font-bold text-green-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5"/> $1M Verified</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600 flex items-center gap-2"><FileText className="w-4 h-4 text-purple-500"/> Pilot Cert.</span>
                                      <span className="text-xs font-bold text-green-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5"/> Active</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal-500"/> Background Check</span>
                                      <span className="text-xs font-bold text-green-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5"/> Passed</span>
                                    </div>
                                  </div>

                                  <div className="p-4 space-y-3 bg-gray-50/50">
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600 flex items-center gap-2"><MapPin className="w-4 h-4 text-rose-500"/> Height Pole</span>
                                      {selectedApplicant.confirms_height_pole || true ? (
                                        <span className="text-xs font-bold text-green-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5"/> Equipped</span>
                                      ) : (
                                        <span className="text-xs font-medium text-gray-400">Not equipped</span>
                                      )}
                                    </div>
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-gray-600 flex items-center gap-2"><Truck className="w-4 h-4 text-indigo-500"/> Certified Vehicle</span>
                                      <span className="text-xs font-bold text-green-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5"/> Confirmed</span>
                                    </div>
                                  </div>

                                </div>
                              </div>
                            </div>

                          </div>

                          <div className="px-4 sm:px-5 pt-3 border-t border-gray-100 bg-white flex-shrink-0">
                            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1.5 block">
                              Assign Dispatcher (optional — enables live chat + GPS tracking with this driver)
                            </label>
                            <select
                              value={selectedDispatcherId}
                              onChange={(e) => setSelectedDispatcherId(e.target.value)}
                              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-blue-100"
                            >
                              <option value="">No dispatcher — assign later</option>
                              {dispatchers.map(d => (
                                <option key={d.id} value={d.id}>{d.full_name} ({d.active_load_count} active)</option>
                              ))}
                            </select>
                            {dispatchers.length === 0 && (
                              <p className="text-[10px] text-amber-600 mt-1.5">
                                No dispatchers on your team yet — add one from the Dispatchers tab.
                              </p>
                            )}
                          </div>

                          <div className="p-4 sm:p-5 border-t border-gray-200 bg-white flex flex-col sm:flex-row gap-3 justify-end flex-shrink-0">
                            <button 
                              className="px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 text-sm font-bold hover:bg-gray-50 transition w-full sm:w-auto"
                              onClick={() => setSelectedApplicant(null)}
                            >
                              Cancel
                            </button>
                            <button 
                              onClick={() => handleAssignDriver(selectedApplicant.driver_id)}
                              disabled={assigningId === selectedApplicant.driver_id}
                              className="px-8 py-2.5 rounded-xl bg-green-600 text-white text-sm font-bold hover:bg-green-700 transition shadow-sm flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto"
                            >
                              {assigningId === selectedApplicant.driver_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                              {assigningId === selectedApplicant.driver_id ? "Assigning..." : "Accept & Assign Driver"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-400 flex-col gap-3 p-6 text-center">
                          <UserCircle className="w-12 h-12 text-gray-300" />
                          <p>Select an applicant from the list to view their experience and certificates.</p>
                        </div>
                      )}
                    </div>

                  </div>
                )}
              </>
            )}
          </div>
        </main>
        
        <BottomNav activeNav="dashboard" setSidebarOpen={setSidebarOpen} handleLogout={handleLogout}/>
      </div>
    </div>
  );
}